import React, { HTMLAttributes, ReactNode, forwardRef } from 'react'
import { FontAwesomeSvgIcon as FontAwesomeIcon } from 'react-fontawesome-svg-icon'

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { VariantProps, cva } from 'class-variance-authority'
import clsx from 'clsx'

type TagVariants = VariantProps<typeof tagVariants>
type IconPositionVariants = VariantProps<typeof iconPositionVariants>

export type TagProps = HTMLAttributes<HTMLDivElement> & {
  children: ReactNode
  /**
   * Add an icon to the Tag
   */
  icon?: IconDefinition
} & TagVariants &
  IconPositionVariants

const tagVariants = cva(
  'w-fit px-2 py-1 flex items-center text-xs leading-none rounded-full whitespace-nowrap font-medium !no-underline',
  {
    defaultVariants: {
      theme: 'default'
    },
    variants: {
      theme: {
        default: 'border border-border-default bg-background-default text-text-alternative',
        error: 'bg-error-muted text-error-default',
        gray: 'border border-border-default bg-background-default text-text-alternative',
        highlight: 'bg-info-muted text-info-default',
        info: 'bg-info-muted text-info-default',
        neutral: 'bg-background-alternative text-text-alternative',
        success: 'bg-success-muted text-success-default',
        warning: 'bg-warning-muted text-warning-default'
      }
    }
  }
)

const iconPositionVariants = cva('', {
  defaultVariants: {
    iconPosition: 'left'
  },
  variants: {
    iconPosition: {
      left: 'order-first mr-2',
      right: 'order-last ml-2'
    }
  }
})
/**
 * Tag - Tags are used to highlight metadata, tags in blogs and roles, invitation status and more. They can often serve as links to other sections related to that tag.
 */
export const Tag = forwardRef<HTMLDivElement, TagProps>(function Tag(
  { children, className, icon, iconPosition, theme, ...props },
  ref
) {
  return (
    <div ref={ref} {...props} className={clsx(tagVariants({ theme }), className)}>
      {icon ? (
        <FontAwesomeIcon className={clsx(iconPositionVariants({ iconPosition }), className)} icon={icon} size='sm' />
      ) : null}
      {children}
    </div>
  )
})

Tag.displayName = 'Tag'
